import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getMyAccountData } from "../api/api";
import { trackEvent } from "../services/amplitude";
import InfoLayout from "../components/info/InfoLayout";
import { normalizeStatus, getStatusLabel } from "../utils/orderStatus";
import "../styles/OrderHistoryPage.css";

export default function OrderHistoryPage({ chatId = "" }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation("orders");

  const fromChatId = location.state?.fromChatId || chatId || "";

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  function formatMoney(value) {
    const n = Number(value || 0);
    const locale = i18n.language?.startsWith("uk") ? "uk-UA" : "en-US";

    return `${new Intl.NumberFormat(locale, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(n)} ${t("currency_symbol")}`;
  }

  useEffect(() => {
    let alive = true;

    async function load() {
      try {
        setLoading(true);

        const data = await getMyAccountData();
        const all = Array.isArray(data?.purchases) ? data.purchases : [];

        const paidOnly = all
          .filter((o) => normalizeStatus(o.status) === "paid")
          .sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));

        if (alive) {
          setOrders(paidOnly);

          trackEvent("purchase_history_opened", {
            orders_count: paidOnly.length,
            has_orders: paidOnly.length > 0,
          });
        }
      } catch (e) {
        console.error(e);

        if (alive) {
          setOrders([]);

          trackEvent("purchase_history_opened", {
            orders_count: 0,
            has_orders: false,
            load_error: true,
          });
        }
      } finally {
        if (alive) setLoading(false);
      }
    }

    load();
    return () => {
      alive = false;
    };
  }, []);

  return (
    <InfoLayout chatId={fromChatId} title={t("history_title")}>
      <div className="ordersPage">
        <div className="ordersList">
          {loading ? (
            Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="orderCard orderCardSkeleton">
                <div className="orderTop">
                  <div className="orderSkeleton orderSkeletonBadge" />
                  <div className="orderSkeleton orderSkeletonTotal" />
                </div>

                <div className="orderSkeleton orderSkeletonMeta" />

                <div className="orderImages">
                  {Array.from({ length: 4 }).map((_, j) => (
                    <div
                      key={j}
                      className="orderImgWrap orderSkeleton orderSkeletonImg"
                    />
                  ))}
                </div>
              </div>
            ))
          ) : orders.length === 0 ? (
            <div className="ordersEmpty">{t("empty")}</div>
          ) : (
            orders.map((o) => (
              <button
                key={o.id}
                type="button"
                className="orderCard orderCardBtn"
                onClick={() =>
                  navigate(`/orders/${encodeURIComponent(o.id)}`, {
                    state: {
                      fromOrders: true,
                      fromChatId: fromChatId,
                      reopenUserMenuOnBack: true,
                    },
                  })
                }
              >
                <div className="orderTop">
                  <div className={`orderStatus st-${normalizeStatus(o.status)}`}>
                    {getStatusLabel(o.status)}
                  </div>

                  <div className="orderTotal">
                    {typeof o.total === "number" ? formatMoney(o.total) : o.total}
                  </div>
                </div>

                <div className="orderMeta">
                  {t("order_number")}
                  {o.receipt_number} • {o.date || ""}
                </div>

                {!!o.items?.length && (
                  <div className="orderImages">
                    {o.items.slice(0, 6).map((it, i) => (
                      <div key={it.id ?? `${o.id}-${i}`} className="orderImgWrap">
                        <img
                          src={it.image_url}
                          alt={it.title || t("fallback_item_lower")}
                          loading="lazy"
                        />
                      </div>
                    ))}
                  </div>
                )}
              </button>
            ))
          )}
        </div>
      </div>
    </InfoLayout>
  );
}